import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import {
  CreditCardIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Package {
  _id: string;
  name: string;
  description: string;
  bannerImage: string;
  priceUSD: number;
  priceAUD: number;
  validityPeriod: number;
  maxGuests: number;
  packageCode: string;
  highlights: string[];
}

const PurchasePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currency, formatPrice } = useCurrency();
  
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    // Customer Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Travel Details
    guests: 2,
    preferredDates: '',
    specialRequests: '',
    
    // Additional Details (Flywire handles payment collection)
    dateOfBirth: '',
    maritalStatus: 'single'
  });

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages/${id}`);
        const data = response.data;
        setPackageData(data.package || data);
      } catch (error) {
        console.error('Failed to fetch package:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPackage();
    }
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Initialize Flywire payment
  const initializeFlywirePayment = async () => {
    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.email) {
        alert('Please fill in all required fields.');
        return;
      }

      // Check if Flywire module is loaded
      if (typeof (window as any).FlywirePayment === "undefined") {
        alert("Flywire payment module is not loaded. Please try again later.");
        return;
      }

      const amount = currency === 'USD' ? (packageData?.priceUSD || 0) : (packageData?.priceAUD || 0);

      // Determine the recipient code based on selected currency (Demo Environment)
      let recipientCode: string;
      let callbackUrl: string;

      switch (currency) {
        case "AUD":
        case "USD":
          // Using demo portal codes from your Flywire demo environment
          recipientCode = "IRG"; // Demo portal code for testing
          callbackUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/flywire-webhook-demo`;
          break;
        default:
          // Fallback to first demo code
          recipientCode = "IRG";
          callbackUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/flywire-webhook-demo`;
          break;
      }

      // Generate a unique transaction ID
      const transactionId = `TXN${Date.now()}`;

      // Construct the return URL with all required parameters for booking creation
      const returnUrl = `${window.location.origin}/payment-success?status=pending&reference=${transactionId}&transactionId=${transactionId}&email=${encodeURIComponent(formData.email)}&name=${encodeURIComponent(`${formData.firstName} ${formData.lastName}`)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData?.name || '')}&packageId=${encodeURIComponent(id || '')}&phone=${encodeURIComponent(formData.phone)}&paymentMethod=Flywire`;

      // Flywire payment configuration (DEMO ENVIRONMENT)
      const config = {
        env: "demo", // Using demo environment for testing
        recipientCode: recipientCode,
        amount: parseFloat(amount.toString()),
        currency: currency.toUpperCase(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        recipientFields: {
          booking_reference: `BALI-${transactionId}`,
          web_source: "baligetaway.com",
          package_id: id,
          package_name: packageData?.name,
          package_code: packageData?.packageCode
        },
        requestPayerInfo: true,
        requestRecipientInfo: false,
        returnUrl: returnUrl,
        callbackUrl: callbackUrl,
        callbackId: `BALI-${transactionId}`,
        callbackVersion: "2",
        onInvalidInput: function(errors: any[]) {
          errors.forEach(function(error) {
            alert(error.msg);
          });
        },
        // Handle successful payment completion
        onSuccess: async (result: any) => {
          console.log('✅ Flywire payment success:', result);
          try {
            const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
            
            // Create booking record immediately
            const bookingResponse = await axios.post('/api/bookings/create', {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              amount: amount,
              currency: currency,
              packageId: id,
              packageName: packageData?.name,
              transactionId: flywirePaymentId,
              paymentMethod: 'flywire'
            });
            
            console.log('✅ Booking created:', bookingResponse.data);
            
            // Redirect to success page
            const successUrl = `/payment-success?status=success&transactionId=${flywirePaymentId}&reference=${bookingResponse.data.booking?.bookingCode || transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData?.name || '')}&paymentMethod=Flywire`;
            window.location.href = successUrl;
          } catch (bookingError: any) {
            console.error('❌ Failed to create booking after payment:', bookingError);
            const successUrl = `/payment-success?status=success&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData?.name || '')}&paymentMethod=Flywire`;
            window.location.href = successUrl;
          }
        },
        // Handle payment cancellation
        onCancel: () => {
          console.log('❌ Flywire payment cancelled by user');
          alert('Payment was cancelled. Please try again when ready.');
        }
      };

      console.log('🧪 DEMO: Initiating Flywire payment with config:', config);
      console.log('🧪 DEMO: Portal Code:', recipientCode);
      console.log('🧪 DEMO: Environment:', 'demo');
      console.log('🧪 DEMO: Callback URL:', callbackUrl);

      // Initiate Flywire modal
      const modal = (window as any).FlywirePayment.initiate(config);
      if (!modal) {
        alert("Failed to initiate payment modal. Please try again.");
        return;
      }
      
      modal.render();

    } catch (error: any) {
      console.error('Payment initialization error:', error);
      alert('Payment failed: ' + (error.response?.data?.message || error.message || 'Please try again'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await initializeFlywirePayment();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading package details...</p>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Package not found</h2>
          <p className="text-gray-600 mb-4">The package you're trying to purchase doesn't exist.</p>
          <button 
            onClick={() => navigate('/packages')}
            className="btn-primary"
          >
            Browse All Packages
          </button>
        </div>
      </div>
    );
  }

  const currentPrice = currency === 'USD' ? packageData.priceUSD : packageData.priceAUD;

  return (
    <>
      <Helmet>
        <title>Purchase {packageData.name} - Bali Getaway</title>
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Purchase</h1>
            <p className="text-gray-600">You're just a few steps away from your dream Bali experience!</p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && <div className={`w-12 h-1 ${currentStep > step ? 'bg-orange-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Customer Details */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-6">
                    <UserIcon className="h-6 w-6 text-orange-500 mr-3" />
                    <h2 className="text-xl font-bold text-gray-900">Customer Details</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Travel Details */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-6">
                    <CalendarIcon className="h-6 w-6 text-orange-500 mr-3" />
                    <h2 className="text-xl font-bold text-gray-900">Travel Details</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Guests *
                      </label>
                      <select
                        name="guests"
                        value={formData.guests}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        {Array.from({ length: packageData.maxGuests }, (_, i) => i + 1).map(num => (
                          <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Travel Dates
                      </label>
                      <input
                        type="date"
                        name="preferredDates"
                        value={formData.preferredDates}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Special Requests or Notes
                    </label>
                    <textarea
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Any special requirements, dietary restrictions, celebrations, etc."
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center mb-6">
                    <CreditCardIcon className="h-6 w-6 text-orange-500 mr-3" />
                    <h2 className="text-xl font-bold text-gray-900">Secure Payment</h2>
                  </div>
                  
                  <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-200">
                    <div className="flex items-center justify-center mb-4">
                      <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold mr-3">
                        🧪 DEMO
                      </div>
                      <img src="/images/flywire-logo.png" alt="Flywire" className="h-8 mr-3" onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }} />
                      <span className="text-lg font-bold text-gray-900">Powered by Flywire</span>
                    </div>
                    
                    <div className="text-center space-y-3">
                      <p className="text-gray-700 font-medium">
                        🧪 Demo Environment - Test payments safely
                      </p>
                      <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-sm text-orange-800">
                        <p className="font-semibold mb-1">Demo Testing Mode:</p>
                        <p>• Use test cards provided by Flywire</p>
                        <p>• No real money will be charged</p>
                        <p>• Portal codes: IRG (USD/AUD demo)</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span>Credit/Debit Cards</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span>Bank Transfers</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span>Digital Wallets</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span>Local Payment Methods</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                        <p className="text-xs text-gray-600 mb-2">You'll be redirected to Flywire's secure payment portal to complete your transaction.</p>
                        <div className="flex justify-center space-x-4">
                          <span className="text-xs text-gray-500">💳 Visa</span>
                          <span className="text-xs text-gray-500">💳 Mastercard</span>
                          <span className="text-xs text-gray-500">💳 Amex</span>
                          <span className="text-xs text-gray-500">🏦 PayPal</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-5 px-8 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      Redirecting to Secure Payment...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <CreditCardIcon className="h-6 w-6 mr-3" />
                      Pay Securely - {formatPrice(packageData.priceUSD, packageData.priceAUD)}
                    </div>
                  )}
                </button>
                
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600">
                    🔐 Your payment is processed securely by Flywire
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    SSL encrypted • PCI compliant • Global payment methods
                  </p>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <img
                      src={packageData.bannerImage}
                      alt={packageData.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{packageData.name}</h3>
                      <p className="text-sm text-gray-600">{packageData.packageCode}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Package Price</span>
                      <span className="font-medium">{formatPrice(packageData.priceUSD, packageData.priceAUD)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Guests</span>
                      <span className="font-medium">{formData.guests}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Validity</span>
                      <span className="font-medium">{packageData.validityPeriod} months</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-orange-600">
                        {formatPrice(packageData.priceUSD, packageData.priceAUD)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-orange-800">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="space-y-1 text-xs">
                        <li>• Your voucher will be emailed within 2 hours</li>
                        <li>• Valid for {packageData.validityPeriod} months from purchase</li>
                        <li>• Flexible booking with resorts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchasePage;
